export type ImportStatus = 'uploading' | 'converting' | 'awaiting_split' | 'creating' | 'done' | 'failed'
export type UploadedFile = { index: number; name: string; size: number; kind: string; seen_before: { import_id: number; created_at: string; drafts: number }[] }

export const ACCEPT = '.docx,.pdf,.odt,.idml,.indd,.zip'
export const MAX_BYTES = 500 * 1024 * 1024
const OK = new Set(ACCEPT.split(','))

export function fileProblem(f: { name: string; size: number }): string | null {
  const dot = f.name.lastIndexOf('.')
  const ext = dot < 0 ? '' : f.name.slice(dot).toLowerCase()
  if (ext === '.doc' || ext === '.rtf') return `${f.name}: save it as .docx and upload that.`
  if (!OK.has(ext)) return `${f.name}: send Word (.docx), PDF, ODT or an InDesign package (.zip).`
  if (f.size > MAX_BYTES) return `${f.name} is over the 500 MB limit.`
  return null
}

const LABELS: Record<ImportStatus, string> = {
  uploading: 'Uploading', converting: 'Reading the files', awaiting_split: 'Waiting for you to check the pieces',
  creating: 'Making the drafts', done: 'Drafts made', failed: 'Could not be read',
}
export const statusLabel = (s: ImportStatus): string => LABELS[s] ?? s

const FILE_LABELS: Record<string, string> = {
  staged: 'Waiting to be read', converted: 'Read', failed: 'Could not be read', expanded: 'Unpacked',
}
/** A file's status in plain words. */
export const fileStatusLabel = (s: string): string => FILE_LABELS[s] ?? 'Waiting to be read'

const WHERE: Record<string, string> = { membership: 'member article', main: 'free site', draft: 'draft' }
/** Where an "already published" match lives, in plain words. */
export const whereLabel = (w: string): string => WHERE[w] ?? 'on the site'

/** The split card's line for a piece that matches something already there. */
export const alreadyText = (a: { title: string; where: string }): string =>
  a.where === 'draft' ? `Already a draft: ${a.title}` : `Already on the site: ${a.title} (${whereLabel(a.where)})`

/** On Done: true when no piece became a draft (each failed or was left out). */
export const nothingMade = (ps: { draft_id: number | null; dropped: boolean }[]): boolean => !ps.some((p) => p.draft_id != null)

/** fetch() has no upload progress; XHR does. */
export function uploadWithProgress(url: string, file: File, onProgress: (pct: number) => void): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)) }
    xhr.onload = () => {
      let body: unknown = xhr.responseText
      try { body = JSON.parse(xhr.responseText) } catch { /* plain text */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as UploadedFile)
      else reject(Object.assign(new Error('upload failed'), { status: xhr.status, detail: body }))
    }
    xhr.onerror = () => reject(Object.assign(new Error('network'), { status: 0, detail: 'The connection dropped.' }))
    const form = new FormData()
    form.append('file', file)
    xhr.send(form)
  })
}

/** The `detail` of an error body: a string, or {code, message}. Works on ApiError.detail and on uploadWithProgress's. */
function bodyDetail(e: { detail?: unknown }): unknown {
  const b = e.detail
  return b && typeof b === 'object' && 'detail' in b ? (b as { detail: unknown }).detail : b
}
const coded = (d: unknown): { code?: string; message?: string } | null => (d && typeof d === 'object' ? d as { code?: string; message?: string } : null)

/** Plain words for a failed upload (413 too big, 422 wrong type, 409 already in this import). */
export function uploadError(name: string, e: { status?: number; detail?: unknown }): string {
  const d = bodyDetail(e)
  if (e.status === 413) return `${name} is over the 500 MB limit.`
  if (e.status === 422) return typeof d === 'string' ? d : `${name}: that kind of file can't be imported.`
  if (e.status === 409) {
    const c = coded(d)
    if (c?.message) return c.message
    if (c?.code === 'duplicate_file') return `${name} is already in this import.`
    if (c?.code === 'imports_off') return 'Import is off for this title.'
  }
  if (e.status === 0) return 'The connection dropped. Try again.'
  return `${name} did not upload.`
}

/** Why an import could not be created (409 imports_off), in plain words. */
export function createError(e: { detail?: unknown }): string {
  return coded(bodyDetail(e))?.code === 'imports_off' ? 'Import is off for this title.' : 'Could not start the import. Try again.'
}

/** After the uploads: carry on, stop and ask, or stay because nothing got in. */
export function afterUploads(kept: number, failed: number): 'go' | 'ask' | 'stay' {
  if (failed === 0) return kept > 0 ? 'go' : 'stay'
  return kept > 0 ? 'ask' : 'stay'
}

/** The server's 422 text, in plain words. */
export function problem(e: unknown): string {
  const d = (e as { detail?: unknown }).detail
  const inner = d && typeof d === 'object' && 'detail' in d ? (d as { detail: unknown }).detail : d
  if (typeof inner === 'string') return inner
  if (Array.isArray(inner) && inner.length) return inner.map((x) => (x && typeof x === 'object' && 'msg' in x ? String((x as { msg: unknown }).msg) : String(x))).join('; ')
  return 'That did not save. Try again.'
}
